import { useState } from 'react'
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Insights from '@mui/icons-material/Insights'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu';
import CssBaseline from '@mui/material/CssBaseline';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ApplicationState } from '../../interface/ApplicationState';
import { useSelector, useDispatch } from 'react-redux';
import { setUserBegin, toggleDrawer } from '../../redux/actions/userActions';
import { BullishBearish } from '../BullishBearish';
import { SubscriptionStatusBadge } from '../SubscriptionStatusBadge';
import { UserProfileModal } from '../modals/UserProfile/UserProfileModal';
import moment from 'moment';
import { CustomAppBar } from './CustomAppBar';
import { DarkModeSwitch } from '../DarkModeSwitch';
import { PairSelector } from './PairSelector';
import SmartBanner from 'react-smartbanner';
import { IntervalSelector } from './IntervalSelector';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';



export const ResponsiveAppBar = () => {
  const theme = useTheme()

  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null);
  const user = useSelector((state: ApplicationState) => state.userStore.user)
  const [ showProfileModal, setShowProfileModal ] = useState(false)
  const isDrawerOpen = useSelector((state: ApplicationState) => state.userStore.isDrawerOpen)
  
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () =>
  {
    localStorage.clear()
    window.location.href = "/"
  }

  const handleCloseProfileModal = () =>
  {
    setShowProfileModal(false)
    setAnchorEl(null);
  }

  return (
    <Box sx={{ display: 'flex' }}>

      <CssBaseline />

      <SmartBanner title="AlgoTrader" />

      <CustomAppBar position="fixed" open={isDrawerOpen}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72, md: 78 },
            px: { xs: 1.5, md: 2.5 },
            gap: 1.5,
          }}
        >
        <Box sx={styles.title}>
        <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => dispatch(toggleDrawer(!isDrawerOpen))}
            edge="start"
            sx={{
              mr: 1.5,
              width: 42,
              height: 42,
              borderRadius: '12px',
              color: theme.palette.text.primary,
              border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.42)}`,
              backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.78),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.info.main,
              backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.14),
              mr: 1,
            }}
          >
            <Insights fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="700" fontSize={{ xs: 24, md: 28 }} lineHeight={1}>
              AlgoTrader
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.text.primary, 0.62), letterSpacing: 1.1 }}>
              Trading command center
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: { xs: 0, md: 1 }, display: { xs: 'none', lg: 'flex' } }}>
          {user && user.lastTakeProfitEvent && (
            <Chip
              size="small"
              label={`Take profit ${moment(user.lastTakeProfitEvent).fromNow()}`}
              sx={styles.chromeChip(theme)}
            />
          )}
          {user?.tradingPair && <Chip size="small" label={user.tradingPair} sx={styles.chromeChip(theme)} />}
          {user?.chartInterval && <Chip size="small" label={user.chartInterval} sx={styles.chromeChip(theme)} />}
        </Stack>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <PairSelector />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <IntervalSelector />
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <SubscriptionStatusBadge />
        </Box>

        { user && user.tradingPair && user.chartInterval && (
          <Box sx={{ display: { xs: 'none', xl: 'block' } }}>
            <BullishBearish marketId={user.tradingPair} interval={user.chartInterval} />
          </Box>
        )} 

        <Box
          sx={{
            px: 1,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.42)}`,
            backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.78),
          }}
        >
          <DarkModeSwitch checked={user.isDarkModeOn} onChange={(e: any) => dispatch(setUserBegin({ isDarkModeOn: e.target.checked } ))} />
        </Box>

        <Avatar src={user.userProfileImageUrl} sx={styles.avatar(theme)} onClick={handleClick}></Avatar>

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose} >
            <MenuItem onClick={() => setShowProfileModal(true)}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>

         
        </Toolbar>

        <UserProfileModal open={showProfileModal} handleClose={handleCloseProfileModal} user={user} />

    </CustomAppBar>
    </Box>

  );
}

const styles = {
  title: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center'
  },
  avatar: (theme: any) => ({
    width: 40,
    height: 40,
    cursor: 'pointer',
    ml: 1,
    mr: 0.5,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.12 : 0.42)}`,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.18 : 0.14),
    boxShadow: 'none',
  }),
  chromeChip: (theme: any) => ({
    borderRadius: '999px',
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.38)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.76),
    color: theme.palette.text.primary,
  })
}
